import { normalizeDeliveryGeography } from '../api/client';

// Short, human labels for a browsing location, built only from what the
// backend returned — nothing is invented when a field is missing.

const PINCODE_PATTERN = /^[0-9]{6}$/;

const clean = (value) => (typeof value === 'string' ? value.trim() : '');

/** A six-digit pincode, or null (digits are never padded or truncated). */
export const toPincode = (value) => {
  const digits = clean(typeof value === 'number' ? String(value) : value).replace(/\s+/g, '');
  return PINCODE_PATTERN.test(digits) ? digits : null;
};

export const isCompletePincode = (value) => PINCODE_PATTERN.test(clean(value));

const join = (...parts) => parts.map(clean).filter(Boolean).join(', ');

const withPincode = (text, pincode) => [clean(text), pincode || ''].filter(Boolean).join(' ');

/** Label for a reverse-geocoded GPS fix (GET /api/places/reverse data). */
export const labelForReverseGeocode = (address) => {
  if (!address) return null;
  const pincode = toPincode(address.pincode);
  const area = join(address.landmark || address.line1, address.city);
  if (area) return withPincode(area, pincode);
  return clean(address.formattedAddress) || pincode || null;
};

/** Label for a chosen search result (GET /api/places/details data). */
export const labelForPlaceDetails = (details, description) => {
  const pincode = toPincode(details?.pincode);
  const area = join(details?.area || details?.street, details?.city);
  if (area) return withPincode(area, pincode);
  return clean(description) || pincode || null;
};

/** Label for a saved account address (GET /api/customer-app/addresses row). */
export const labelForSavedAddress = (address) => {
  if (!address) return null;
  const place = withPincode(clean(address.city), toPincode(address.pincode));
  return join(address.label, place || address.line1) || null;
};

/** One-line address text for a saved address, in the site's existing style. */
export const savedAddressSummary = (address) =>
  [address?.line1, address?.line2, address?.city, address?.state, address?.pincode]
    .map(clean)
    .filter(Boolean)
    .join(', ');

/** A label that reveals no more than the delivery area itself. */
export const genericLocationLabel = (pincode) => toPincode(pincode);

/**
 * A saved address as a browsing location, or null when it carries no usable
 * geography (no six-digit pincode and no pinned coordinates). Pure mapping:
 * nothing about the account — including which address is default — changes.
 */
export const locationFromSavedAddress = (address, { confirmedByUser }) => {
  const geography = normalizeDeliveryGeography({
    pincode: toPincode(address?.pincode),
    coordinates: address?.coordinates,
  });
  if (!geography) return null;
  return {
    ...geography,
    source: 'address',
    addressId: address?._id ? String(address._id) : null,
    label: labelForSavedAddress(address),
    confirmedByUser: confirmedByUser === true,
  };
};

/** What the header shows for a location. */
export const displayLabelFor = ({ label, pincode }) =>
  clean(label) || toPincode(pincode) || 'Selected location';
